import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { psychologistSelectorById } from 'src/redux/specialists/selector';
import { ModalSheet, useEvent } from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import {
  MapStatusContractToUIStatus,
  useSignAgreementsContext,
} from 'src/context/SignAgreementsContext/SignAgreements.context';
import { SignAgreementWidget } from 'src/widgets';
import { updateSpecialistBy } from 'src/redux/specialists/reducer';
import { updateLoadedSpecialistBy } from 'src/redux/loadedSpecialists/reducer';

type ContractWithSpecialistProps = {
  specialistId: string;
  onAgreementAccepted?: () => void;
};

export type ContractWithSpecialistRef = {
  openContract: () => void;
  applyContract: () => Promise<void>;
};

const ContractWithSpecialist = forwardRef<ContractWithSpecialistRef, ContractWithSpecialistProps>(
  ({ specialistId, onAgreementAccepted }, ref) => {
    const specialist = useSelector(psychologistSelectorById(specialistId));
    const {
      closeModal,
      currentOpenModal,
      openModal,
      openContractById,
      isSignAgreementOpen,
      createContractBySpecialistId,
      fetchStatus,
    } = useSignAgreementsContext();
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const clientSpecialistContractStatus = specialist?.client?.specialist_client_contract_status
      ? MapStatusContractToUIStatus[specialist?.client?.specialist_client_contract_status]
      : 'not_signed';

    useImperativeHandle(ref, () => ({
      openContract: openContract,
      applyContract: handleApplyContract,
    }));

    const openContract = useEvent(() => {
      if (
        (clientSpecialistContractStatus === 'active' && specialist?.client?.specialist_client_contract_id) ||
        (clientSpecialistContractStatus === 'expired' && specialist?.client?.specialist_client_contract_id)
      ) {
        openContractById(specialist.client?.specialist_client_contract_id, specialist.id, i18n.language);
        return;
      }
      openModal('schedule');
    });

    const handleApplyContract = useCallback(async () => {
      if (clientSpecialistContractStatus === 'not_signed' && specialist) {
        const data = await createContractBySpecialistId(specialist.id);
        dispatch(
          updateSpecialistBy({
            field: 'id',
            value: specialist.id,
            data: {
              client: {
                specialist_client_contract_status: data.status,
                specialist_client_contract_id: data.id,
              },
            },
          })
        );
        dispatch(
          updateLoadedSpecialistBy({
            field: 'id',
            value: specialist.id,
            data: {
              client: {
                specialist_client_contract_status: data.status,
                specialist_client_contract_id: data.id,
              },
            },
          })
        );
      }
      onAgreementAccepted?.();
      closeModal();
    }, [clientSpecialistContractStatus, specialist, createContractBySpecialistId, onAgreementAccepted, dispatch]);

    if (!specialist) return null;

    return (
      <ModalSheet
        isOpen={isSignAgreementOpen && currentOpenModal === 'schedule'}
        onDidDismiss={closeModal}
        withCloseButton
      >
        <SignAgreementWidget
          type={!specialist?.client?.specialist_client_contract_id ? 'new' : 'last'}
          specialistId={specialist.id}
          // @ts-ignore
          contractStatus={clientSpecialistContractStatus}
          isLoading={fetchStatus === 'pending'}
          onApply={handleApplyContract}
        />
      </ModalSheet>
    );
  }
);

export default ContractWithSpecialist;
